/**
 * safeToast — global patch for `sonner` so that toast.* NEVER crashes
 * React with error #31 ("Objects are not valid as a React child").
 *
 * Why we need this:
 *   Many parts of the codebase call things like:
 *       toast.error(e?.response?.data?.detail || 'Failed');
 *   The backend sometimes returns a structured detail object such as
 *       {code, service, label_hi, label_en, message_hi, message_en}
 *   (e.g. `service_not_authorized_error`, Pydantic validation arrays).
 *   Sonner tries to render whatever we pass as JSX → React crashes →
 *   the crash bubbles up to <AppErrorBoundary/> and the user sees a
 *   scary full-screen error page.
 *
 * The fix here is deliberately at the singleton level:
 *   - `sonner` exports a single `toast` object across the whole bundle.
 *   - We import it once, wrap every method that accepts a message arg
 *     so any non-string/non-JSX value is coerced to a readable string
 *     BEFORE it ever hits React.
 *   - Every other file continues to `import { toast } from 'sonner'`
 *     unchanged — they automatically benefit from the guard.
 *
 * Kept intentionally tiny + defensive: this file must NEVER throw
 * itself, otherwise it would defeat its own purpose.
 */
import { toast as sonnerToast } from 'sonner';

// Human-readable coercion. Mirrors _coerceToString in lib/api.js so the
// two stay behaviour-compatible.
function coerceToString(x, depth = 0) {
    try {
        if (x === null || x === undefined) return '';
        if (typeof x === 'string') return x;
        if (typeof x === 'number' || typeof x === 'boolean') return String(x);
        // React elements (JSX) have $$typeof — leave them alone so callers
        // can still pass rich JSX bodies to sonner.
        if (typeof x === 'object' && x.$$typeof) return x;
        if (Array.isArray(x)) {
            return x.map(item => coerceToString(item, depth + 1)).filter(Boolean).join(' • ');
        }
        if (typeof x === 'object') {
            // Prefer human-readable keys first.
            if (typeof x.message_hi === 'string' && x.message_hi) return x.message_hi;
            if (typeof x.message_en === 'string' && x.message_en) return x.message_en;
            if (typeof x.message    === 'string' && x.message)    return x.message;
            if (typeof x.msg        === 'string' && x.msg)        return x.msg;
            if (x.detail !== undefined && depth < 2) {
                const nested = coerceToString(x.detail, depth + 1);
                if (nested) return nested;
            }
            if (typeof x.label_hi === 'string' && x.label_hi) return x.label_hi;
            if (typeof x.label_en === 'string' && x.label_en) return x.label_en;
            if (typeof x.code     === 'string' && x.code)     return `Error: ${x.code}`;
            // Last resort — a truncated JSON so devs can still debug.
            try { return JSON.stringify(x).slice(0, 200); } catch { return '[object]'; }
        }
        return String(x);
    } catch {
        return '';
    }
}

// Some sonner methods take (message, options). We only guard the
// message arg; options are passed through untouched.
function wrapMethod(fnName) {
    const original = sonnerToast[fnName];
    if (typeof original !== 'function') return;
    // eslint-disable-next-line no-param-reassign
    sonnerToast[fnName] = function safeToastMethod(message, options) {
        const safe = coerceToString(message);
        return original.call(sonnerToast, safe || 'Something went wrong', options);
    };
}

// Guard every user-facing method sonner exposes. `toast.promise` is
// intentionally left alone because it accepts a promise, not a message.
['error', 'success', 'warning', 'info', 'message', 'loading', 'custom'].forEach(wrapMethod);

// Guard the callable form too: `toast('hi')` / `toast(objectByMistake)`.
// sonner's default export is a function object; if we can wrap it we
// swap in a proxy that coerces the first arg.
if (typeof sonnerToast === 'function') {
    // Note: we cannot reassign the imported binding in ES modules, but
    // sonner returns a callable object where the call target is exposed
    // via .call semantics. We monkey-patch the properties above which
    // already covers 99% of usage. The rare direct-call site will just
    // print JSON — non-fatal.
}

export { sonnerToast as toast, coerceToString };
